import { OptionFormTypes } from '../../services/filtering/filter-form-render-service/option-form-types';
import { WarehouseStatus } from '../../containers/orders-filter-bar-container/filter-options/options/warehouse-status';
import { VOID_TYPE, PACKAGE_TYPE, MULTI_PACKAGE, PICK_BATCH, PICK_BATCH_QUEUE } from '../../filters/constants';
import { REFUND_ASSIST_STATUS_IDS } from '../../components/shipping-grid/columns/void-type';

export const isNullIndicator = 'empty';
export const isNotNullIndicator = 'notEmpty';
export const containsIndicator = 'equals';
export const notContainsIndicator = 'notEquals';

// To cover the gap between the new filter contract and the old, we need to handle columns that don't match v2
const collectionFilterColumnsThatNeedMapping = {
  isResidential: 'ResidentialIndicator',
};

export const criteriaForCollectionFilterType = (filter) => {
  const { filterValue, criterionOperatorValue, criterionArgumentValue } = filter;
  const column = collectionFilterColumnsThatNeedMapping[filterValue] || filterValue;

  // FulfillmentType (Shipping Mode): 'Parcel' is a synthetic checklist value.
  // Translate it to a negation of FreightLtl because Parcel = NOT FreightLtl.
  // isIn:false generates: (fp.FillMethod IS NULL OR fp.FillMethod != 3)
  if (column === 'FulfillmentType') {
    const values = Array.isArray(criterionArgumentValue) ? criterionArgumentValue : [criterionArgumentValue];
    const hasFreight = values.includes('FreightLtl');
    const hasParcel = values.includes('Parcel');
    if (hasParcel && !hasFreight) {
      return { collectionFilters: { column, isIn: false, values: ['FreightLtl'] } };
    }
    if (hasFreight && !hasParcel) {
      return { collectionFilters: { column, isIn: true, values: ['FreightLtl'] } };
    }
    // Both selected = all orders; send all known FillMethod values → no-op filter
    return { collectionFilters: { column, isIn: true, values: ['FreightLtl', 'Shipment', 'External'] } };
  }

  if ([isNullIndicator, isNotNullIndicator].includes(criterionOperatorValue)) {
    return {
      collectionFilters: {
        column,
        isNull: criterionOperatorValue === isNullIndicator,
      },
    };
  }
  if ([containsIndicator, notContainsIndicator].includes(criterionOperatorValue)) {
    return {
      collectionFilters: {
        column,
        isIn: criterionOperatorValue === containsIndicator,
        values: criterionArgumentValue,
      },
    };
  }
  return {
    collectionFilters: {
      column,
      values: criterionArgumentValue,
    },
  };
};

export const getBooleanOperatorValuesForCollectionFilter = (filter) => {
  const { criterionOperatorValue } = filter;
  if ([isNullIndicator, isNotNullIndicator].includes(criterionOperatorValue)) {
    return {
      isIn: false,
      isNull: criterionOperatorValue === isNullIndicator,
    };
  }
  if ([containsIndicator, notContainsIndicator].includes(criterionOperatorValue)) {
    return {
      isIn: criterionOperatorValue === containsIndicator,
      isNull: undefined,
    };
  }
  return {
    isIn: true,
    isNull: undefined,
  };
};

const isCollectionFilterType = (filterType) => {
  return (
    filterType === OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER || filterType === OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER_OR_BLANK
  );
};

const isNotCollectionFilterType = (filterType) => {
  return (
    filterType !== OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER && filterType !== OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER_OR_BLANK
  );
};

export const normalizeCollectionFilters = (collectionFilters = []) => {
  return Object.values(
    collectionFilters.reduce((normalizedCollectionFilters, collectionFilter) => {
      const groupKey = `${collectionFilter.column}:${collectionFilter.isIn}:${collectionFilter.isNull}`;
      const group = normalizedCollectionFilters[groupKey];
      if (!group) {
        return {
          ...normalizedCollectionFilters,
          [groupKey]: {
            column: collectionFilter.column,
            isIn: collectionFilter.isIn,
            isNull: collectionFilter.isNull,
            values: collectionFilter.values,
          },
        };
      }
      normalizedCollectionFilters[groupKey].values.push(...collectionFilter.values);
      return normalizedCollectionFilters;
    }, {}),
  );
};

const findFilterIndex = ({ filterById, criterionOperatorValue, id }, filter) => {
  if (filter.id) {
    return filter.id === id;
  }
  return filter.filterById === filterById && filter.criterionOperatorValue === criterionOperatorValue;
};

const isMultiPackageFilterEntry = (filter) => filter.filterById === PACKAGE_TYPE && filter.criterionArgumentValue === MULTI_PACKAGE;
const isPickBatchQueueFilterEntry = (filter) => filter.filterById === PICK_BATCH && filter.criterionArgumentValue === PICK_BATCH_QUEUE;

export const reduceCollectionFilterTypeIfExists = (filters = []) => {
  const orderCollectionFilters = filters.filter((filter) => isCollectionFilterType(filter.type));
  if (!orderCollectionFilters.length) {
    return filters;
  }
  const resultingFilters = filters.filter((filter) => isNotCollectionFilterType(filter.type));

  const multiPackageEntries = orderCollectionFilters.filter(isMultiPackageFilterEntry);
  const pickBatchQueueEntries = orderCollectionFilters.filter(isPickBatchQueueFilterEntry);
  const regularCollectionFilters = orderCollectionFilters.filter((f) => !isMultiPackageFilterEntry(f) && !isPickBatchQueueFilterEntry(f));

  const groupedCollectionFilters = regularCollectionFilters.reduce((acc, filter) => {
    const index = acc.findIndex((f) => findFilterIndex(f, filter));

    // Handle void type with isNullIndicator - when criterionArgumentValue is the null indicator string,
    // it should be treated as the criterionOperatorValue instead
    const isNullVoidType = filter.criterionArgumentValue === isNullIndicator;

    const criterionArgumentValue = [];
    const value = !Array.isArray(filter.criterionArgumentValue) ? [filter.criterionArgumentValue] : filter.criterionArgumentValue;
    // Only add values if it's not a null indicator (which should be in operator, not argument)
    if (!isNullVoidType && filter.criterionArgumentValue != null) {
      criterionArgumentValue.push(...value);
    }

    const isEmptyWarehouseStatus = filter.filterById === WarehouseStatus.id && filter.criterionArgumentValue === undefined;
    const criterionOperatorValue = isEmptyWarehouseStatus || isNullVoidType ? isNullIndicator : filter.criterionOperatorValue;

    if (index === -1) {
      return [...acc, { ...filter, criterionArgumentValue, criterionOperatorValue }];
    }

    // oxlint-disable-next-line no-param-reassign
    acc[index] = {
      ...acc[index],
      criterionArgumentValue: [...acc[index].criterionArgumentValue, ...criterionArgumentValue],
      criterionOperatorValue,
    };
    return acc;
  }, []);

  // Special handling for void type: if both Manual and Refund Assist are selected,
  // remove both filters (user wants ALL void types, so no filtering needed)
  // Check the original orderCollectionFilters to see ALL selected void type filters
  const voidTypeFilters = orderCollectionFilters.filter((f) => f.filterById === VOID_TYPE);
  const hasManualVoidType = voidTypeFilters.some(
    (f) => f.criterionArgumentValue === isNullIndicator || f.criterionArgumentValue?.value === isNullIndicator,
  );
  const hasRefundAssistVoidType = voidTypeFilters.some((f) => {
    const actualValue = f.criterionArgumentValue?.value ?? f.criterionArgumentValue;
    return (
      Array.isArray(actualValue) &&
      actualValue.length === REFUND_ASSIST_STATUS_IDS.length &&
      actualValue.every((id) => REFUND_ASSIST_STATUS_IDS.includes(id))
    );
  });

  // If both void types are selected, remove void type filters entirely
  const finalGroupedFilters =
    hasManualVoidType && hasRefundAssistVoidType
      ? groupedCollectionFilters.filter((f) => f.filterById !== VOID_TYPE)
      : groupedCollectionFilters;

  const multiPackageFilters =
    multiPackageEntries.length > 0
      ? [
          {
            ...multiPackageEntries[0],
            filterValue: MULTI_PACKAGE,
            criterionArgumentValue: ['true'],
            criterionOperatorValue: containsIndicator,
          },
        ]
      : [];

  const pickBatchQueueFilters =
    pickBatchQueueEntries.length > 0
      ? [
          {
            ...pickBatchQueueEntries[0],
            filterValue: PICK_BATCH_QUEUE,
            criterionArgumentValue: ['true'],
            criterionOperatorValue: containsIndicator,
          },
        ]
      : [];

  return [...resultingFilters, ...finalGroupedFilters, ...multiPackageFilters, ...pickBatchQueueFilters];
};
